/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { inject } from '@angular/core';
import { combineLatest, map, of, take } from 'rxjs';
import { ActivatedRouteSnapshot, CanActivateFn, createUrlTreeFromSnapshot } from '@angular/router';

import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { IsAdminService } from 'src/app/common/fancy-firebase/custom/is-admin.service';
import { IProjectSettingTab } from 'src/app/modules/project-settings/interfaces/project-settings';
import {
	DATA_SOURCES_TAB,
	TIME_SCHEDULE_TAB,
} from 'src/app/modules/project-settings/constants/project-settings';

export const projectSettingsGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
	const isAdminService = inject(IsAdminService);
	const projectId = route?.params.projectId;
	const featureFlagManager = inject(FeatureFlagManager);

	if (!projectId) {
		return createUrlTreeFromSnapshot(route, ['/projects']);
	}

	return combineLatest([
		// Note: isAdminService.isAdminOnProject$ is not working with deep linking
		// this causes this guard blocks the user from accessing the project settings
		// even when the user is an admin on the project, just in deep linking cases
		isAdminService.isAdminOnProject$(of(projectId)),
		featureFlagManager.getFlag$(DynamicFlagTypes.PROJECT_SETTINGS_ENABLED),
	]).pipe(
		map(([isAdmin, isProjectSettingsEnabled]) => {
			return (isAdmin && isProjectSettingsEnabled) || createUrlTreeFromSnapshot(route, ['/projects']);
		})
	);
};

export const projectSettingsTabGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
	const isAdminService = inject(IsAdminService);
	const projectId = route.parent?.params.projectId;

	if (!projectId) {
		return createUrlTreeFromSnapshot(route, ['/projects']);
	}

	return isAdminService.isAdminOnProject$(of(projectId)).pipe(
		take(1),
		map((isAdmin) => {
			const tabConfig = [DATA_SOURCES_TAB, TIME_SCHEDULE_TAB].find(
				(tab: IProjectSettingTab) => tab.route === route.routeConfig.path
			);
			return !tabConfig?.isAdminOnly || isAdmin || createUrlTreeFromSnapshot(route, ['/projects']);
		})
	);
};
